import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ProjectMetadataService } from '../../../../shared/project-metadata.service';
import { ClientLoggingService } from '../../../../shared/client-logging.service';

export interface IngestionSettings {
  enableVisualVerification?: boolean;
  enableRegionHierarchy?: boolean;
}

/** Card for toggling ingestion pipeline features at the project level (Issue #1151, #1228). */
@Component({
  selector: 'app-ingestion-settings-card',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule, MatSlideToggleModule, MatProgressBarModule],
  templateUrl: './ingestion-settings-card.component.html',
  styleUrl: './ingestion-settings-card.component.scss',
})
export class IngestionSettingsCardComponent {
  @Input() projectId = '';
  @Input() ingestionSettings: IngestionSettings | null = null;

  saving = false;
  error = '';

  private projectMetadataService = inject(ProjectMetadataService);

  get visualVerificationEnabled(): boolean {
    return this.ingestionSettings?.enableVisualVerification ?? false;
  }

  get regionHierarchyEnabled(): boolean {
    return this.ingestionSettings?.enableRegionHierarchy ?? false;
  }

  async onVisualVerificationToggle(enabled: boolean): Promise<void> {
    await this.saveIngestionSetting('enableVisualVerification', enabled);
  }

  async onRegionHierarchyToggle(enabled: boolean): Promise<void> {
    await this.saveIngestionSetting('enableRegionHierarchy', enabled);
  }

  private async saveIngestionSetting(key: keyof IngestionSettings, enabled: boolean): Promise<void> {
    if (this.saving) return;

    this.saving = true;
    this.error = '';
    try {
      // Send both values to avoid proto3 default-false wiping the other toggle
      // (backend replaces ingestion_settings wholesale, not per-field merge).
      const fullSettings: IngestionSettings = {
        enableVisualVerification: this.visualVerificationEnabled,
        enableRegionHierarchy: this.regionHierarchyEnabled,
        [key]: enabled,
      };
      await this.projectMetadataService.updateProjectMetadata(this.projectId, {
        ingestionSettings: fullSettings,
      });

      if (!this.ingestionSettings) {
        this.ingestionSettings = {};
      }
      this.ingestionSettings[key] = enabled;

      ClientLoggingService.log('INFO', `[IngestionSettingsCard] ${key} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      ClientLoggingService.log('ERROR', '[IngestionSettingsCard] Failed to save:', err);
      this.error = 'Failed to save ingestion settings.';
    } finally {
      this.saving = false;
    }
  }
}
